import styled from '@emotion/styled';
import { getImage } from '@utils/imageHelper';
import { mediaQuery, Spacings, Fonts } from '@styles/index';
import BaseImage from '@base/Image';
import Typography from 'aquilla/core/atoms/Typography';
import { WrapItem, Wrap } from 'aquilla/core/atoms/Wrap';
import Stack from 'aquilla/core/atoms/Stack';

import BackGroundImage from './BackgroundImage';

const MissionBlockImage = 'about/device_mashup.png';

const Container = styled.div`
  margin-top: ${Spacings.SPACING_20B};
  grid-column: span 24;
  position: relative;
  ${mediaQuery.uptoTablet} {
    margin-top: ${Spacings.SPACING_19B};
  }
`;

const MissionGroup = styled.div`
  grid-column: span 13;
  margin-top: ${Spacings.SPACING_16B};
  margin-bottom: ${Spacings.SPACING_20B};
  ${mediaQuery.uptoTablet} {
    grid-column: span 24;
    margin-bottom: 42px;
    margin-top: ${Spacings.SPACING_8B};
  }
`;

const Mission = styled(Typography)`
  font-weight: ${Fonts.BOLD};
  font-size: ${Fonts.LARGE};
  line-height: ${Fonts.HEADER_LINE_HEIGHT};
  letter-spacing: 2px;
  color: var(--color-i-green);
  ${mediaQuery.uptoTablet} {
    display: block;
    font-size: ${Fonts.ULTRA_SMALL};
    text-align: center;
  }
`;

const Description = styled(Typography)`
  font-weight: ${Fonts.BOLD};
  font-size: ${Fonts.LARGE_HEADING};
  line-height: ${Fonts.PARA_LINE_HEIGHT};
  color: var(--color-i-black);
  margin-top: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoTablet} {
    font-size: ${Fonts.H3};
    text-align: center;
  }
  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_4B};
  }
`;

const CardHolder = styled(Wrap)`
  width: 480px;
  ${mediaQuery.uptoTablet} {
    grid-column: span 24;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    row-gap: ${Spacings.SPACING_10B};
    padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_4B};
  }
`;

const Card = styled(WrapItem)`
  background-color: var(--color-i-white);
  border: ${Spacings.SPACING_10};
  box-shadow: ${Spacings.SPACING_0B} ${Spacings.SPACING_4B}
    ${Spacings.SPACING_8B} rgba(221, 230, 237, 0.4);
  border-radius: ${Spacings.SPACING_2B};
  width: 224px;
  height: 131px;
  padding: ${Spacings.SPACING_5B} ${Spacings.SPACING_6B};
  border-color: red;
  ${mediaQuery.uptoTablet} {
    width: auto;
    box-shadow: none;
    padding: ${Spacings.SPACING_0B};
    flex-grow: 2;
    background: transparent;
    height: ${Spacings.SPACING_20B};
  }
`;

const Text = styled(Typography)`
  font-weight: ${Fonts.BOLD};
  font-size: ${Fonts.H1};
  line-height: ${Fonts.PARA_LINE_HEIGHT};
  color: ${({ $color }) => $color};
  ${mediaQuery.uptoTablet} {
    font-size: ${Fonts.LARGE_HEADING};
    text-align: center;
    display: block;
  }
`;

const SubText = styled(Typography)`
  font-weight: ${Fonts.SEMIBOLD};
  font-size: ${Fonts.H4};
  line-height: ${Fonts.HEADER_LINE_HEIGHT};
  color: var(--color-text-secondary);
  display: block;
  margin-top: ${Spacings.SPACING_1B};
  ${mediaQuery.uptoTablet} {
    font-size: ${Fonts.LARGE};
    text-align: center;
    color: var(--color-i-violet-1);
    font-weight: ${Fonts.LIGHT};
    margin-top: ${Spacings.SPACING_2B};
  }
`;

const PictureContainer = styled.div`
  grid-column: 13/24;
  flex: 1;
  text-align: center;
  ${mediaQuery.uptoTablet} {
    grid-column: span 12;
    margin-top: ${Spacings.SPACING_14B};
    padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_4B};
  }
`;

const Image = styled(BaseImage)`
  height: 278px;
  width: 560px;
  object-fit: contain;
  grid-column: 13/24;
  ${mediaQuery.uptoTablet} {
    grid-column: span 12;
    margin-top: ${Spacings.SPACING_14B};
    padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_4B};
    width: 100%;
    height: auto;
  }
`;

const Flex = styled.div`
  display: flex;
  justify-content: center;
`;

const Space = styled.span`
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const infos = [
  { text: '50M', color: 'var(--color-i-green)', subText: 'Active Learners' },
  { text: '1000', color: 'var(--color-i-blue)', subText: 'Top Educators' },
  { text: '1B', color: 'var(--color-i-orange-1)', subText: 'Minutes Watched' },
  { text: '2000', color: 'var(--color-i-red)', subText: 'Daily live Classes' }
];

const MissionBlock = () => {
  return (
    <Container>
      <BackGroundImage src={getImage('about/quote.png')} />
      <Stack>
        <MissionGroup>
          <Mission>OUR MISSION &amp; IMPACT</Mission>
          <Description variant="h2">
            Democratise access to high <br /> quality education
          </Description>
        </MissionGroup>
        <Stack orientation="horizontal">
          <CardHolder spacing="16px">
            {infos.map((info) => (
              <Card key={info.text}>
                <Flex>
                  <Text $color={info.color}>{info.text}</Text>
                  <Space>&nbsp;</Space>
                  <Text $color={info.color}>+</Text>
                </Flex>
                <SubText>{info.subText}</SubText>
              </Card>
            ))}
          </CardHolder>
          <PictureContainer>
            <Image
              src={MissionBlockImage}
              alt="Unacademy"
              height={278}
              width={560}
            />
          </PictureContainer>
        </Stack>
      </Stack>
    </Container>
  );
};

export default MissionBlock;
